import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  getCurrentSubscription, 
  getAllTiers, 
  upgradeSubscription, 
  getUsageStats,
  SUBSCRIPTION_TIERS 
} from '../services/shared/subscriptionService';
import '../styles/accessibility.css';

const Subscription = () => {
  const navigate = useNavigate();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [tiers] = useState(getAllTiers());
  const [selectedTier, setSelectedTier] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: '',
    cardName: '',
    expiryDate: '',
    cvv: ''
  });

  useEffect(() => {
    document.title = 'Subscription Plans - Trust Tech Studio';
    loadSubscriptionData();
  }, []);

  const loadSubscriptionData = () => {
    const subscription = getCurrentSubscription();
    const stats = getUsageStats();
    setCurrentSubscription(subscription);
    setUsageStats(stats);
  };

  const handleSelectPlan = (tier) => {
    if (tier.id === 'free') {
      // Free tier - no payment needed
      handleUpgrade(tier.id);
    } else {
      setSelectedTier(tier);
      setShowPaymentModal(true);
    }
  };

  const handleUpgrade = (tierId, paymentMethod = null) => {
    const result = upgradeSubscription(tierId, paymentMethod);
    
    if (result.success) {
      alert(`Successfully upgraded to ${SUBSCRIPTION_TIERS[tierId.toUpperCase()].name}!`);
      setShowPaymentModal(false);
      loadSubscriptionData();
    } else {
      alert(`Failed to upgrade: ${result.error}`);
    }
  };

  const handlePayment = (e) => {
    e.preventDefault();
    
    // Basic validation
    if (!paymentDetails.cardNumber || !paymentDetails.cardName || 
        !paymentDetails.expiryDate || !paymentDetails.cvv) {
      alert('Please fill in all payment details');
      return;
    }

    // In production, this would integrate with Stripe/PayPal
    const paymentMethod = {
      type: 'card',
      last4: paymentDetails.cardNumber.slice(-4),
      brand: 'visa',
      expiryDate: paymentDetails.expiryDate
    };

    handleUpgrade(selectedTier.id, paymentMethod);
  };

  const getDiscountedPrice = (price, cycle) => {
    if (cycle === 'yearly') {
      return Math.round(price * 12 * 0.8); // 20% discount for yearly
    }
    return price;
  };

  const getTierBadge = (tierId) => {
    if (!currentSubscription) return null;
    if (currentSubscription.tierId === tierId) {
      return <span className="badge bg-success ms-2">Current Plan</span>;
    }
    return null;
  };

  const getPriceDisplay = (tier) => {
    const price = getDiscountedPrice(tier.price, billingCycle);
    if (price === 0) return 'Free';
    if (billingCycle === 'yearly') {
      return `$${price}/year`;
    }
    return `$${price}/month`;
  };

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h1 className="display-6 fw-bold mb-2">
                <i className="bi bi-stars me-2 text-primary"></i>
                Subscription Plans
              </h1>
              <p className="text-muted mb-0">
                Choose the perfect plan for your trust management needs
              </p>
            </div>
            <Link to="/trust-tech-studio" className="btn btn-outline-secondary">
              <i className="bi bi-arrow-left me-1"></i>
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>

      {/* Current Subscription Info */}
      {currentSubscription && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm bg-gradient-to-r from-purple-50 to-blue-50">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-8">
                    <h5 className="mb-2">
                      Current Plan: {SUBSCRIPTION_TIERS[currentSubscription.tierId.toUpperCase()]?.name}
                    </h5>
                    <p className="text-muted mb-2">
                      <i className="bi bi-calendar-check me-2"></i>
                      {usageStats?.daysRemaining} days remaining
                    </p>
                  </div>
                  <div className="col-md-4 text-md-end">
                    <div className="mb-2">
                      <small className="text-muted">Scans this month:</small>
                      <div className="progress mt-1" style={{ height: '8px' }}>
                        <div 
                          className="progress-bar" 
                          style={{ width: `${Math.min(usageStats?.scans.percentage || 0, 100)}%` }}
                        ></div>
                      </div>
                      <small className="text-muted">
                        {usageStats?.scans.used} / {usageStats?.scans.limit === -1 ? '∞' : usageStats?.scans.limit}
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Billing Cycle Toggle */}
      <div className="row mb-4">
        <div className="col-12 text-center">
          <div className="btn-group" role="group">
            <button 
              className={`btn ${billingCycle === 'monthly' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button 
              className={`btn ${billingCycle === 'yearly' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly <span className="badge bg-success ms-1">Save 20%</span>
            </button>
          </div>
        </div>
      </div>

      {/* Pricing Cards */}
      <div className="row g-4 mb-4">
        {tiers.map((tier) => (
          <div key={tier.id} className="col-12 col-md-6 col-xl-3">
            <div className={`card h-100 ${currentSubscription?.tierId === tier.id ? 'border-primary shadow-lg' : 'border-0 shadow-sm'}`}>
              <div className="card-body d-flex flex-column">
                <div className="text-center mb-4">
                  <h4 className="mb-2">
                    {tier.name}
                    {getTierBadge(tier.id)}
                  </h4>
                  <div className="display-4 fw-bold mb-2">
                    {tier.price === 0 ? (
                      'Free'
                    ) : (
                      <>
                        <small className="fs-5">$</small>
                        {billingCycle === 'yearly' ? Math.round(tier.price * 12 * 0.8 / 12) : tier.price}
                      </>
                    )}
                  </div>
                  <small className="text-muted">
                    {tier.price === 0 ? '14-day trial' : `per user/${billingCycle === 'yearly' ? 'month billed yearly' : 'month'}`}
                  </small>
                  {billingCycle === 'yearly' && tier.price > 0 && (
                    <div className="mt-2">
                      <small className="text-success">
                        <i className="bi bi-check-circle me-1"></i>
                        Save ${Math.round(tier.price * 12 * 0.2)} per year
                      </small>
                    </div>
                  )}
                </div>

                <ul className="list-unstyled mb-4 flex-grow-1">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="mb-2">
                      <i className="bi bi-check-circle-fill text-success me-2"></i>
                      <small>{feature}</small>
                    </li>
                  ))}
                </ul>

                <button 
                  className={`btn w-100 ${currentSubscription?.tierId === tier.id ? 'btn-outline-primary' : 'btn-primary'}`}
                  onClick={() => handleSelectPlan(tier)}
                  disabled={currentSubscription?.tierId === tier.id}
                >
                  {currentSubscription?.tierId === tier.id ? 'Current Plan' : tier.price === 0 ? 'Start Free Trial' : 'Upgrade Now'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Feature Comparison Table */}
      <div className="row">
        <div className="col-12">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <h4 className="mb-4">Feature Comparison</h4>
              <div className="table-responsive">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Feature</th>
                      {tiers.map(tier => (
                        <th key={tier.id} className="text-center">{tier.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>Scans per month</td>
                      <td className="text-center">5</td>
                      <td className="text-center">50</td>
                      <td className="text-center">Unlimited</td>
                      <td className="text-center">Unlimited</td>
                    </tr>
                    <tr>
                      <td>Users</td>
                      <td className="text-center">1</td>
                      <td className="text-center">3</td>
                      <td className="text-center">10</td>
                      <td className="text-center">Unlimited</td>
                    </tr>
                    <tr>
                      <td>Data retention</td>
                      <td className="text-center">7 days</td>
                      <td className="text-center">30 days</td>
                      <td className="text-center">90 days</td>
                      <td className="text-center">Unlimited</td>
                    </tr>
                    <tr>
                      <td>Advanced analytics</td>
                      <td className="text-center"><i className="bi bi-x text-danger"></i></td>
                      <td className="text-center"><i className="bi bi-x text-danger"></i></td>
                      <td className="text-center"><i className="bi bi-check text-success"></i></td>
                      <td className="text-center"><i className="bi bi-check text-success"></i></td>
                    </tr>
                    <tr>
                      <td>API access</td>
                      <td className="text-center"><i className="bi bi-x text-danger"></i></td>
                      <td className="text-center"><i className="bi bi-x text-danger"></i></td>
                      <td className="text-center"><i className="bi bi-check text-success"></i></td>
                      <td className="text-center"><i className="bi bi-check text-success"></i></td>
                    </tr>
                    <tr>
                      <td>Custom reports</td>
                      <td className="text-center"><i className="bi bi-x text-danger"></i></td>
                      <td className="text-center"><i className="bi bi-check text-success"></i></td>
                      <td className="text-center"><i className="bi bi-check text-success"></i></td>
                      <td className="text-center"><i className="bi bi-check text-success"></i></td>
                    </tr>
                    <tr>
                      <td>Support</td>
                      <td className="text-center">Community</td>
                      <td className="text-center">Email</td>
                      <td className="text-center">Priority</td>
                      <td className="text-center">24/7 Phone</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {showPaymentModal && selectedTier && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Complete Payment</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowPaymentModal(false)}
                ></button>
              </div>
              <form onSubmit={handlePayment}>
                <div className="modal-body">
                  <div className="mb-3">
                    <h6>Selected Plan: {selectedTier.name}</h6>
                    <p className="text-muted">
                      {getPriceDisplay(selectedTier)}
                    </p>
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Cardholder Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={paymentDetails.cardName}
                      onChange={(e) => setPaymentDetails({...paymentDetails, cardName: e.target.value})}
                      required
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Card Number</label>
                    <input
                      type="text"
                      className="form-control"
                      maxLength="16"
                      placeholder="1234 5678 9012 3456"
                      value={paymentDetails.cardNumber}
                      onChange={(e) => setPaymentDetails({...paymentDetails, cardNumber: e.target.value.replace(/\D/g, '')})}
                      required
                    />
                  </div>
                  
                  <div className="row">
                    <div className="col-6">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="MM/YY"
                        maxLength="5"
                        value={paymentDetails.expiryDate}
                        onChange={(e) => setPaymentDetails({...paymentDetails, expiryDate: e.target.value})}
                        required
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">CVV</label>
                      <input
                        type="text"
                        className="form-control"
                        maxLength="3"
                        placeholder="123"
                        value={paymentDetails.cvv}
                        onChange={(e) => setPaymentDetails({...paymentDetails, cvv: e.target.value.replace(/\D/g, '')})}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="mt-3">
                    <small className="text-muted">
                      <i className="bi bi-shield-check me-1"></i>
                      Your payment information is secure and encrypted
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowPaymentModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="bi bi-credit-card me-1"></i>
                    Pay {getPriceDisplay(selectedTier)}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Subscription;
